import { BRAND_TELEGRAM_URL } from "@/lib/brandConfig";
import type { PremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { normalizePublicOutboundUrl } from "@/lib/telegramPublicInviteUrl";

/**
 * Resuelve la URL de "Join free" / canal Telegram gratis para CTAs tipo Mux.
 *
 * Cadena de prioridad (de más específica a fallback):
 * 1. Admin `premium_invite_config.free_telegram_url`
 * 2. Env `VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL` (Railway build)
 * 3. Env `VITE_TELEGRAM_FREE_GROUP_URL` (canal gratis compartido con BioTemplatePremiumSimple)
 * 4. Env `VITE_BRAND_TELEGRAM_URL` (fallback de marca)
 * 5. `BRAND_TELEGRAM_URL` construido desde handle
 *
 * Ana Fernández: el canal público gratis es `https://t.me/anaafernandezz`
 * (doble z). No confundir con `BRAND_SOURCE_KEY` (`anaafernandez`, una z).
 */
export function resolveFreeJoinTelegramUrl(
  publicConfig: PremiumInvitePublicConfig | null | undefined,
): string | undefined {
  // Toggle maestro del "Elemento free-telegram": false = ocultar en todo el sitio.
  if (publicConfig?.free_telegram_enabled === false) return undefined;
  const fromAdmin = publicConfig?.free_telegram_url?.trim() ?? "";
  const fromPremiumEnv = (
    import.meta.env.VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined
  )?.trim() ?? "";
  const fromFreeGroupEnv = (
    import.meta.env.VITE_TELEGRAM_FREE_GROUP_URL as string | undefined
  )?.trim() ?? "";
  const fromBrandEnv = (
    import.meta.env.VITE_BRAND_TELEGRAM_URL as string | undefined
  )?.trim() ?? "";
  const fromBrand = BRAND_TELEGRAM_URL?.trim() ?? "";
  const merged = (
    fromAdmin ||
    fromPremiumEnv ||
    fromFreeGroupEnv ||
    fromBrandEnv ||
    fromBrand
  ).trim();
  const normalized = normalizePublicOutboundUrl(merged);
  return normalized || undefined;
}

/**
 * Per-country free URL override map.
 * Shape: `Record<ISO2, string>` stored in `premium_invite_config.free_telegram_url_by_country`.
 */
export function resolveFreeJoinTelegramUrlByCountry(
  publicConfig: PremiumInvitePublicConfig | null | undefined,
  countryCode: string | null | undefined,
): string | undefined {
  // Toggle maestro del "Elemento free-telegram": false = ocultar en todo el sitio.
  if (publicConfig?.free_telegram_enabled === false) return undefined;
  const code =
    typeof countryCode === "string" ? countryCode.trim().toUpperCase().slice(0, 2) : "";
  if (!code) return resolveFreeJoinTelegramUrl(publicConfig);

  const byCountry = publicConfig?.free_telegram_url_by_country;
  if (byCountry && typeof byCountry === "object" && !Array.isArray(byCountry)) {
    const override = (byCountry as Record<string, string>)[code];
    if (override) {
      const normalized = normalizePublicOutboundUrl(String(override));
      if (normalized) return normalized;
    }
  }
  return resolveFreeJoinTelegramUrl(publicConfig);
}
